import { useCurrentDate, Card, H6 } from "@sellar-io/components";
import {
  SupplierRelationshipFulfillmentType,
  SupplierRelationshipPaymentTermsType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  getNextCourierDispatchAndDeliveryDates,
  pluralize,
} from "@sellar-io/common";
import { ReactElement, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useFeatureFlagStore } from "../../hooks/useFeatureFlags";
import { StorefrontPaymentNotesDialog } from "../../pages/stockList/categoryPage/Storefront/StorefrontCards/StorefrontPaymentNotesDialog";
import { Icon } from "../index";
import { PaymentTermsIndicator } from "../paymentTypeIndicator/PaymentTermsIndicator";
import { usePaymentTermsIndicator } from "../paymentTypeIndicator/usePaymentTermsIndicator";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import dayjs from "dayjs";
import { TooltipContainer } from "@sellar-io/components";

type PaymentTermsCardConnectionType = {
  SupplierRetailerConnectionPaymentTermsRequest: {
    paymentDueCalculateFrom:
      | "endOfMonth"
      | "invoiceIssueDate"
      | "endOfFollowingMonth";
    paymentDue: number;
    status: "pending" | "accepted" | "rejected";
  } | null;
  approved: boolean;
  fulfillmentType: SupplierRelationshipFulfillmentType;
  paymentDue: number | null;
  paymentDueCalculateFrom: SupplierRelationshipPaymentDueCalculateFromType | null;
  paymentTerms: SupplierRelationshipPaymentTermsType | null;
};

type SupplierPaymentTermsType = SupplierGetResponseType & {
  CourierDeliverySchedule:
    | SupplierStorefrontV2GetResponseType["CourierDeliverySchedule"]
    | null;
};
export const PaymentTermsCard = ({
  className,
  supplier,
  connection,
  isImplicitProformaActive = false,
}: {
  className?: string;
  supplier: SupplierPaymentTermsType | undefined;
  connection: PaymentTermsCardConnectionType | undefined | null;
  isImplicitProformaActive?: boolean;
}): ReactElement | null => {
  const { isActive } = useFeatureFlagStore();
  const [paymentNotesActive, setPaymentNotesActive] = useState<boolean>(false);

  // Check for Sellar Credit eligibility (needed for implicit proforma logic)
  const retailerCreditFlagEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );
  const { business } = useSellarCurrentBusiness();

  if (!supplier) return null;

  // no payment options shown for sellar import suppliers
  if (supplier.onlyAllowCardPaymentWithDeposit) return null;

  const invoiceCreationStage = supplier.invoiceCreationStage;

  // When implicit proforma is active, check if we should show the card
  // Show for: Sellar Credit or Supplier Credit (credit terms)
  // Don't show for: pro-forma, null terms, paymentOnDelivery
  if (isImplicitProformaActive) {
    const sellarCreditEnabled =
      retailerCreditFlagEnabled &&
      business?.BusinessUser?.canAccessSellarCredit &&
      !!business.Retailer &&
      !supplier.hasOptedOutSellarCredit;

    const showCard =
      sellarCreditEnabled || connection?.paymentTerms === "credit";

    if (!showCard) {
      return null;
    }
  }

  return (
    <Card className={twMerge("bg-white", "flex flex-col gap-4 p-4", className)}>
      {isActive("CHECKOUT_CREDIT_TERMS_REQUEST") ? null : (
        <PaymentTermsIndicator
          connection={connection}
          invoiceCreationStage={invoiceCreationStage}
          supplier={supplier}
        />
      )}
      <StorefrontPaymentTermsCardContent
        connection={connection}
        supplier={supplier}
        isImplicitProformaActive={isImplicitProformaActive}
      />
      {supplier.paymentNotes && (
        <>
          <p
            onClick={() => setPaymentNotesActive(true)}
            className={
              "m-0 flex cursor-pointer flex-row items-center gap-2 p-0 text-blue-800"
            }
          >
            <Icon icon={"OPEN_IN_NEW"} />
            <p className={"font-semibold"}>MORE INFO</p>
          </p>
          <StorefrontPaymentNotesDialog
            active={paymentNotesActive}
            onDismiss={() => setPaymentNotesActive(false)}
            supplierId={supplier.id}
          />
        </>
      )}
    </Card>
  );
};

const StorefrontPaymentTermsCardContent = ({
  connection,
  supplier,
  isImplicitProformaActive,
}: {
  connection: PaymentTermsCardConnectionType | undefined | null;
  supplier: SupplierPaymentTermsType;
  isImplicitProformaActive: boolean;
}) => {
  const { isActive } = useFeatureFlagStore();
  const invoiceCreationStage = supplier.invoiceCreationStage;
  const isConnectionPaymentTermPending =
    connection?.SupplierRetailerConnectionPaymentTermsRequest?.status ===
    "pending";
  const showConnectionPaymentTermsPending =
    connection && !connection.approved && isConnectionPaymentTermPending;
  const { full } = usePaymentTermsIndicator({
    connection: {
      ...connection,
      paymentDue: connection?.paymentDue ?? null,
      paymentDueCalculateFrom: connection?.paymentDueCalculateFrom ?? null,
      paymentTerms: connection?.paymentTerms ?? null,
    },
    invoiceCreationStage: supplier.invoiceCreationStage,
    approved: connection?.approved ?? false,
    paymentTermsRequest: showConnectionPaymentTermsPending
      ? connection.SupplierRetailerConnectionPaymentTermsRequest
      : null,
    hasDefaultPriceTier: !!supplier.CustomerZones[0],
    supplier,
  });

  const retailerCreditFlagEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );
  const { business } = useSellarCurrentBusiness();

  const sellarCreditEnabled =
    retailerCreditFlagEnabled &&
    business?.BusinessUser?.canAccessSellarCredit &&
    !!business.Retailer &&
    !supplier.hasOptedOutSellarCredit;

  const getCopy = () => {
    if (sellarCreditEnabled) {
      return <SellarCreditPaymentTerms supplier={supplier} />;
    }

    if (isImplicitProformaActive) {
      // When implicit proforma is active, only show explicit credit terms
      // Sellar Credit is handled above, now handle Supplier Credit
      if (connection?.paymentTerms === "credit") {
        return (
          <SupplierCreditPaymentTerms
            connection={connection}
            supplier={supplier}
          />
        );
      }
      // For pro-forma, null, or paymentOnDelivery - don't show card
      return null;
    }

    if (showConnectionPaymentTermsPending) {
      return (
        <p>
          You can place your order <strong>pro-forma</strong> if your credit
          terms have not been approved yet
        </p>
      );
    }
    if (connection?.paymentTerms === "proforma" || !connection?.paymentTerms) {
      if (isActive("CHECKOUT_CREDIT_TERMS_REQUEST")) {
        return (
          <>
            {connection?.SupplierRetailerConnectionPaymentTermsRequest ? (
              <p>Payment requested before finalisation of order</p>
            ) : (
              <PaymentTermsProformaOrPOD
                title={
                  <p>
                    Pay <strong>pro-forma</strong> upfront, or
                  </p>
                }
              />
            )}
          </>
        );
      } else {
        return <H6>Payment requested before finalisation of order</H6>;
      }
    }

    if (connection.paymentTerms === "paymentOnDelivery") {
      if (isActive("CHECKOUT_CREDIT_TERMS_REQUEST")) {
        return (
          <PaymentTermsProformaOrPOD
            title={
              <p>
                Make your payment up to or on the{" "}
                <strong>date of delivery</strong>, or
              </p>
            }
          />
        );
      } else {
        return <H6>Make your payment up to or on the date of delivery</H6>;
      }
    }

    if (isActive("CHECKOUT_CREDIT_TERMS_REQUEST")) {
      return (
        <PaymentTermsCredit
          paymentDueCalculateFrom={connection.paymentDueCalculateFrom}
          paymentDue={connection.paymentDue}
        />
      );
    }

    return (
      <H6>
        Invoice issued on{" "}
        {invoiceCreationStage === "dispatched"
          ? "dispatch"
          : invoiceCreationStage === "delivered"
            ? "delivery"
            : "finalisation"}{" "}
        of order.
      </H6>
    );
  };

  const copyContent = getCopy();

  // When implicit proforma is active and getCopy returns null, don't render anything
  if (isImplicitProformaActive && !copyContent) {
    return null;
  }

  return (
    <>
      {isActive("CHECKOUT_CREDIT_TERMS_REQUEST") && <span>{full}</span>}
      {copyContent}
    </>
  );
};

const PaymentTermsProformaOrPOD = ({
  title,
}: {
  title: string | ReactNode;
}) => {
  return (
    <div className={"flex flex-col gap-4"}>
      <p className={"text-base"}>{title}</p>
      <div className="rounded-lg border border-dashed p-3">
        <p className={"text-sm"}> Add your agreed credit terms at checkout</p>
      </div>
    </div>
  );
};

const PaymentTermsCredit = ({
  paymentDueCalculateFrom,
  paymentDue,
}: {
  paymentDueCalculateFrom: SupplierRelationshipPaymentDueCalculateFromType | null;
  paymentDue: number | null;
}) => {
  const getPaymentDueCalString = () => {
    switch (paymentDueCalculateFrom) {
      case "endOfMonth":
        return "End of month";
      case "endOfFollowingMonth":
        return "End of following month";
      default:
        return "Invoice issue date";
    }
  };

  const paymentDueString = `+ ${paymentDue ?? 0} ${pluralize("day", paymentDue ?? 0)}`;

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-lime-300 bg-lime-100 p-3">
          <p className={"text-lime-700"}>
            <strong>
              {getPaymentDueCalString()} {paymentDueString}
            </strong>{" "}
            credit
          </p>
        </div>
        <p>
          Or, pay <strong>pro-forma</strong> upfront
        </p>
      </div>
    </>
  );
};

const SellarCreditTooltip = () => {
  return (
    <span className="relative top-1 inline-flex">
      <TooltipContainer
        enable
        placement="top"
        contentClassName="max-w-64 p-2 text-center"
        text="Payment due date calculated from estimated order dispatch"
      >
        <Icon icon="INFORMATION_OUTLINE" className="text-zinc-500" />
      </TooltipContainer>
    </span>
  );
};

const SellarCreditPaymentTerms = ({
  supplier,
}: {
  supplier: SupplierPaymentTermsType;
}) => {
  const currentDate = useCurrentDate();
  const courierDeliverySchedule = supplier.CourierDeliverySchedule;
  const courierDeliveryScheduleDays =
    !!courierDeliverySchedule?.CourierDeliveryScheduleDay.length;
  const showDeliverySchedule =
    !!courierDeliverySchedule && courierDeliveryScheduleDays;
  const courierDeliveryScheduleEstimations = showDeliverySchedule
    ? getNextCourierDispatchAndDeliveryDates({
        orderCutOffTime: dayjs(courierDeliverySchedule.orderCutOffTime)
          .utc()
          .format("HH:mm"),
        courierDaysSchedule: courierDeliverySchedule.CourierDeliveryScheduleDay,
        dispatchDaysAfterConfirmation:
          courierDeliverySchedule.dispatchDaysAfterConfirmation,
        deliveryDaysAfterDispatch:
          courierDeliverySchedule.deliveryDaysAfterDispatch,
        startDateReference: currentDate,
        allowSaturdayDelivery: courierDeliverySchedule.allowSaturdayDelivery,
      })
    : null;

  const estimatedCourierDelivery =
    courierDeliveryScheduleEstimations?.estimatedDispatchDate.toDate();

  const creditDueDays = dayjs(estimatedCourierDelivery ?? currentDate)
    .endOf("month")
    .add(30, "days")
    .diff(dayjs(currentDate), "days");

  const getNumberOfPaymentOptions = () => {
    let number = 1;
    if (supplier.stripeAccountId) number++;
    return number;
  };

  return (
    <>
      <p>
        Buy now, pay in{" "}
        <strong>
          {creditDueDays} {pluralize("day", creditDueDays)}
        </strong>{" "}
        <SellarCreditTooltip /> whilst {supplier.Business.name} gets paid
        upfront
      </p>
      <p className="text-sm">
        +{getNumberOfPaymentOptions()} other payment{" "}
        {pluralize("option", getNumberOfPaymentOptions())} available
      </p>
    </>
  );
};

const SupplierCreditPaymentTerms = ({
  connection,
  supplier,
}: {
  connection: PaymentTermsCardConnectionType;
  supplier: SupplierPaymentTermsType;
}) => {
  const paymentDue = connection.paymentDue ?? 0;
  const invoiceCreationStage = supplier.invoiceCreationStage;

  const getInvoiceStageText = () => {
    switch (invoiceCreationStage) {
      case "dispatched":
        return "dispatch";
      case "delivered":
        return "delivery";
      case "confirmed":
      default:
        return "finalisation";
    }
  };

  return (
    <p>
      Invoice issued on {getInvoiceStageText()} of order by{" "}
      {supplier.Business.name}. Payment due {paymentDue}{" "}
      {pluralize("day", paymentDue)} thereafter.
    </p>
  );
};
